import { useMemo } from 'react';
import { Formik, Form, useFormikContext } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { DockerSecurityFormFields } from './DockerSecurityFormFields';
import { DockerSecurityFormValues } from './types';
import { useDockerSecurityInitialValues } from './useDockerSecurityInitialValues';
import { createDockerSecurityValidationSchema } from './dockerSecurityValidationSchema';

interface Props {
  /** Form submission handler */
  onSubmit: (values: DockerSecurityFormValues) => Promise<void>;
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Existing policy for edit mode - provides initial values */
  policy?: TypedPolicy<unknown>;
  /** Template data for pre-filling form in create mode */
  templateData?: Record<string, unknown>;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back is enabled - only used in create mode */
  canGoBack?: boolean;
}

/**
 * Docker Security Policy Form Component
 *
 * Used by:
 * - PolicyCreateView (step 2): mode="create" with wizard navigation props
 * - ConfigTab (edit view): mode="edit" without wizard navigation
 */
export function DockerSecurityPolicyForm({
  onSubmit,
  mode,
  policy,
  templateData,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useDockerSecurityInitialValues(policy, templateData);
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createDockerSecurityValidationSchema(nameValidation),
    [nameValidation]
  );

  return (
    <Formik<DockerSecurityFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <DockerSecurityPolicyInnerForm
        mode={mode}
        policyId={policy?.Id ?? null}
        originalEnvironmentGroupIds={originalEnvironmentGroupIds}
        onBack={onBack}
        canGoBack={canGoBack}
      />
    </Formik>
  );
}

/**
 * Inner component that has access to Formik context.
 * Renders Widget with form fields and PolicyFormFooter.
 */
function DockerSecurityPolicyInnerForm({
  mode,
  policyId,
  originalEnvironmentGroupIds,
  onBack,
  canGoBack,
}: {
  mode: 'create' | 'edit';
  policyId: number | null;
  originalEnvironmentGroupIds: Array<number>;
  onBack?: () => void;
  canGoBack?: boolean;
}) {
  const { values, setFieldValue } =
    useFormikContext<DockerSecurityFormValues>();

  return (
    <Form className="form-horizontal">
      <Widget>
        <WidgetBody>
          {mode === 'create' && (
            <PolicyConfigurationFormSection policyType="security-docker" />
          )}
          <DockerSecurityFormFields
            formData={values}
            onChange={(key, value) => setFieldValue(key, value)}
          />
          {/* Edit mode: footer inside Widget */}
          {mode === 'edit' && (
            <PolicyFormFooter
              mode={mode}
              policyType="security-docker"
              policyId={policyId}
              originalEnvironmentGroupIds={originalEnvironmentGroupIds}
            />
          )}
        </WidgetBody>
      </Widget>

      {/* Create mode: footer outside Widget with StickyFooter */}
      {mode === 'create' && (
        <PolicyFormFooter
          mode={mode}
          policyType="security-docker"
          policyId={policyId}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      )}
    </Form>
  );
}
